import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash'
import SearchIcon from '@mui/icons-material/Search'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'

import { formatNumber } from 'src/utils/formatter-utils'
import {
    ActionsRowResponsive,
    Breadcrumbs,
    buttonSx,
    chipSx,
    classToFComponent,
    dataGridRowHoverSx,
    dataGridSx,
    FCClassContext,
    inputSx,
    LifesHubCard,
    LifesHubConfirmDialog,
    LifesHubEmptyState,
    ModuleView,
    PageSubtitle,
    PageTitle,
    SkeletonBlock,
    switchSx
} from 'src/utils/views'

import UserManagerScope, { UserRow } from '../um_scopes'

const LOG = Logger.get('UserManager-VIEW')

const PageView = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '26px 28px 42px',
    overflowX: 'hidden',
    overflowY: 'auto',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    '@media (max-width: 720px)': {
        padding: '22px 18px 36px'
    }
})

const PageHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    marginBottom: 4,
    flexShrink: 0,
    '@media (max-width: 720px)': {
        flexDirection: 'column',
        '& > div:last-of-type': { width: '100%' }
    }
})

const FiltersCard = styled(LifesHubCard)({
    padding: 18,
    flexShrink: 0
})

const TableCard = styled(LifesHubCard)({
    flexGrow: 1,
    minHeight: 420
})

const useStyles = makeStyles()({
    filterRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 2.1fr) minmax(220px, .9fr) auto',
        gap: 12,
        alignItems: 'end',
        '@media (max-width: 1050px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
        }
    },
    field: {
        display: 'grid',
        gap: 6,
        minWidth: 0
    },
    fieldLabel: {
        fontSize: 12,
        fontWeight: 700,
        color: '#303733'
    },
    input: {
        ...inputSx
    },
    switchField: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minHeight: 43,
        minWidth: 0
    },
    switchLabel: {
        margin: 0,
        fontWeight: 700,
        fontSize: 12,
        color: '#303733'
    },
    help: {
        fontSize: 11.5,
        color: 'var(--muted)'
    },
    searchButton: {
        '@media (max-width: 1050px)': {
            justifySelf: 'start'
        }
    },
    resultsCount: {
        marginTop: 15,
        color: 'var(--green-800)',
        fontWeight: 700
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
        padding: 18
    },
    grid: {
        ...dataGridSx,
        ...dataGridRowHoverSx,
        '& .MuiDataGrid-cell.actions': {
            padding: '0 6px'
        }
    },
    flagCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        color: 'var(--green-700)'
    },
    flagOff: {
        color: 'var(--muted-2)'
    },
    skeletonFilters: {
        height: 118,
        flexShrink: 0
    },
    skeletonTable: {
        flexGrow: 1,
        minHeight: 420
    }
})

type UserManagerViewProps = {
    className?: string
    scope: UserManagerScope
}

class UserManagerClass implements FCClassContext<UserManagerViewProps> {
    // :: emissors
    readonly emitFind = () => {
        const nameFilter = this.nameFilterInputRef.current?.value || ''
        this.scope.onFind(nameFilter).catch(LOG.caught)
    }

    readonly emitNewClick = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly emitEditClick = (id: GridRowId) => () => {
        this.scope.onEdit(Number(id)).catch(LOG.caught)
    }

    readonly emitDeleteConfirmTextChange = (value: string) => {
        this.scope.onDeleteConfirmTextChanged(value).catch(LOG.caught)
    }

    readonly emitCancelConfirmDeleteClick = () => {
        this.scope.onCancelConfirmDelete().catch(LOG.caught)
    }

    readonly emitConfirmDeleteClick = () => {
        this.scope.onConfirmDelete().catch(LOG.caught)
    }

    readonly emitDeleteClick = (id: GridRowId) => () => {
        this.scope.onDelete(Number(id)).catch(LOG.caught)
    }

    readonly emitShowDeletedChange = () => {
        this.scope.onShowDeletedChange().catch(LOG.caught)
    }

    // :: Fields
    scope!: UserManagerScope
    classes!: ReturnType<typeof useStyles>['classes']
    columns = static_build_columns(this.emitEditClick, this.emitDeleteClick)
    nameFilterInputRef!: React.RefObject<HTMLInputElement | null>

    // :: Methods

    onSyncState({ scope }: UserManagerViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render({ className, scope }: UserManagerViewProps) {
        this.scope = bindUpdate(React, scope)
        this.nameFilterInputRef = React.useRef<HTMLInputElement>(null)

        const restoringUser = !!scope.userToDelete?.deleted_date

        return (
            <ModuleView className={className}>
                <PageView>
                    {this.renderPageHead()}
                    {scope.waiting ? this.renderSkeleton() : this.renderContent()}
                </PageView>
                <LifesHubConfirmDialog
                    open={scope.deleteModalOpen}
                    title={restoringUser ? 'Restaurar usuário' : 'Excluir usuário'}
                    subtitle={scope.userToDelete?.name}
                    tone={restoringUser ? 'blue' : 'danger'}
                    message={
                        restoringUser
                            ? 'O usuário volta a aparecer na listagem e recupera o acesso ao sistema.'
                            : 'O usuário sai da listagem e perde o acesso ao sistema. A operação pode ser desfeita depois, restaurando o usuário.'
                    }
                    confirmWord={restoringUser ? 'RESTORE' : 'DELETE'}
                    typedWord={scope.deleteConfirmText}
                    onTypedWordChange={this.emitDeleteConfirmTextChange}
                    confirmLabel={restoringUser ? 'Restaurar usuário' : 'Excluir usuário'}
                    onCancel={this.emitCancelConfirmDeleteClick}
                    onConfirm={this.emitConfirmDeleteClick}
                />
            </ModuleView>
        )
    }

    renderPageHead() {
        return (
            <PageHead>
                <div>
                    <Breadcrumbs>
                        Administração / <b>Usuários</b>
                    </Breadcrumbs>
                    <PageTitle>Usuários</PageTitle>
                    <PageSubtitle>
                        Consulte quem tem acesso ao sistema, quais módulos cada pessoa enxerga e a qual grupo de
                        franquia ela responde.
                    </PageSubtitle>
                </div>
                <ActionsRowResponsive>{this.renderNewButton()}</ActionsRowResponsive>
            </PageHead>
        )
    }

    renderNewButton() {
        return (
            <Button sx={buttonSx('brand')} startIcon={<AddIcon />} onClick={this.emitNewClick}>
                Novo usuário
            </Button>
        )
    }

    renderSkeleton() {
        const { classes } = this

        return (
            <>
                <SkeletonBlock className={classes.skeletonFilters} />
                <SkeletonBlock className={classes.skeletonTable} />
            </>
        )
    }

    renderContent() {
        return (
            <>
                {this.renderFilters()}
                {this.renderTable()}
            </>
        )
    }

    renderFilters() {
        const { classes, scope } = this

        return (
            <FiltersCard elevation={0}>
                <div className={classes.filterRow}>
                    <div className={classes.field}>
                        <label className={classes.fieldLabel} htmlFor="userNameFilter">
                            Buscar
                        </label>
                        <TextField
                            id="userNameFilter"
                            className={classes.input}
                            fullWidth
                            defaultValue={scope.userFilter}
                            inputRef={this.nameFilterInputRef}
                            placeholder="Buscar por nome de usuário ou nome completo"
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 20, color: 'var(--muted)' }} />
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />
                    </div>
                    <div className={classes.field}>
                        <div className={classes.switchField}>
                            <Switch
                                sx={switchSx}
                                size="small"
                                checked={scope.showDeleted}
                                onChange={this.emitShowDeletedChange}
                                slotProps={{
                                    input: { 'aria-label': 'Mostrar excluídos' }
                                }}
                            />
                            <div>
                                <div className={classes.switchLabel}>Mostrar excluídos</div>
                                <div className={classes.help}>Inclui na lista os usuários já removidos do sistema.</div>
                            </div>
                        </div>
                    </div>
                    <Button
                        className={classes.searchButton}
                        sx={buttonSx()}
                        startIcon={<SearchIcon />}
                        onClick={this.emitFind}
                    >
                        Buscar
                    </Button>
                </div>
                <div className={classes.resultsCount}>
                    {pluralize(scope.users.length, 'usuário encontrado', 'usuários encontrados')}
                </div>
            </FiltersCard>
        )
    }

    renderTable() {
        const { classes, scope } = this

        return (
            <TableCard elevation={0}>
                {scope.users.length === 0 ? (
                    <LifesHubEmptyState
                        title="Nenhum usuário encontrado"
                        text={
                            scope.userFilter
                                ? 'Nenhum usuário corresponde ao texto buscado. Limpe a busca para ver a lista completa.'
                                : 'Ainda não há usuários cadastrados com os critérios atuais.'
                        }
                        actions={this.renderNewButton()}
                    />
                ) : (
                    <div className={classes.cardBody}>
                        <DataGrid
                            className={classes.grid}
                            rows={scope.users}
                            columns={this.columns}
                            getRowId={static_getRowId}
                            disableRowSelectionOnClick
                            density="compact"
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 25
                                    }
                                }
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                        />
                    </div>
                )}
            </TableCard>
        )
    }
}

export const UserManagerView = classToFComponent(UserManagerClass, React)
export default UserManagerView

function pluralize(count: number, singular: string, plural: string): string {
    return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

function FlagCell({ granted, label }: { granted: boolean; label: string }) {
    const { classes } = useStyles()

    return (
        <span className={clsx(classes.flagCell, !granted && classes.flagOff)}>
            {granted ? (
                <CheckIcon sx={{ fontSize: 18 }} aria-label={`${label}: sim`} />
            ) : (
                <span aria-label={`${label}: não`}>—</span>
            )}
        </span>
    )
}

function flagColumn(field: string, headerName: string, width: number, description?: string): GridColDef {
    return {
        field,
        headerName,
        description,
        align: 'center',
        headerAlign: 'center',
        width,
        renderCell(params) {
            return <FlagCell granted={!!params.value} label={headerName} />
        }
    }
}

function static_build_columns(
    handleEditClick: (id: GridRowId) => () => void,
    emitDeleteClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'name',
            headerName: 'Usuário',
            width: 220,
            renderCell({ value }) {
                const label = (value as string) ?? '—'
                return (
                    <span style={{ fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
                        {label}
                    </span>
                )
            }
        },
        {
            field: 'full_name',
            headerName: 'Nome completo',
            width: 250
        },
        {
            field: 'preferred_group_name',
            headerName: 'Grupo franquia',
            width: 170,
            renderCell({ value }) {
                return value ? <span>{value as string}</span> : <span style={{ color: 'var(--muted-2)' }}>—</span>
            }
        },
        {
            field: 'active',
            headerName: 'Situação',
            align: 'center',
            headerAlign: 'center',
            width: 120,
            renderCell(params) {
                if (params.row.deleted_date) {
                    return <Chip size="small" label="Excluído" sx={chipSx('red')} />
                }

                return params.value ? (
                    <Chip size="small" label="Ativo" sx={chipSx('green')} />
                ) : (
                    <Chip size="small" label="Inativo" sx={chipSx('gray')} />
                )
            }
        },
        flagColumn('compliant', 'Compliance', 110, 'Acesso a dados protegidos por compliance'),
        flagColumn('the_connection', 'Relações Societárias', 160, 'Relações Societárias (The Connection)'),
        flagColumn('the_map', 'The Map', 100, 'The Map'),
        flagColumn('analise_desempenho', 'Análise de Desempenho', 180, 'Análise de Desempenho (operadoras ANS)'),
        flagColumn('the_acting', 'Profissionais Médicos', 170, 'Profissionais Médicos (The Acting)'),
        flagColumn(
            'the_acting_print',
            'Prof. Médicos — impressão',
            190,
            'Profissionais Médicos: permissão de impressão (The Acting Print)'
        ),
        flagColumn('the_consumer_market', 'Mercado IA', 130, 'Mercado IA (The Consumer Market)'),
        flagColumn('mercado_vet', 'Mercado Veterinário', 170, 'Mercado Veterinário'),
        flagColumn('user_manager', 'Gerenciar Usuários', 160, 'Gerenciar Usuários'),
        flagColumn('can_export_data', 'Exportar dados', 140, 'Permissão de exportar dados'),
        flagColumn('tcm_filtro_cnpj_modo_clonagem', 'CNPJ Clonagem', 150, 'Filtro de CNPJ em modo clonagem'),
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 96,
            cellClassName: 'actions',
            getActions: ({ id, row }) => {
                return [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={
                            <Tooltip title="Editar usuário">
                                <EditOutlinedIcon sx={{ fontSize: 20 }} />
                            </Tooltip>
                        }
                        label="Editar usuário"
                        onClick={handleEditClick(id)}
                        style={{ color: 'var(--green-800)' }}
                    />,
                    <GridActionsCellItem
                        key={'del' + id}
                        onClick={emitDeleteClick(id)}
                        icon={
                            row.deleted_date ? (
                                <Tooltip title="Restaurar usuário">
                                    <RestoreFromTrashIcon sx={{ fontSize: 20 }} />
                                </Tooltip>
                            ) : (
                                <Tooltip title="Excluir usuário">
                                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                </Tooltip>
                            )
                        }
                        label={row.deleted_date ? 'Restaurar usuário' : 'Excluir usuário'}
                        style={{ color: row.deleted_date ? 'var(--blue)' : 'var(--danger)' }}
                    />
                ]
            }
        }
    ]
}

function static_getRowId(row: UserRow) {
    return row.id + ''
}
